// @ts-nocheck
import { remapSandboxIds } from './sandboxIds';
import { expectOk } from './http';
import { normalizeTime, dateFromIso, addDays, alignScenarioWeekStart } from './datetime';
import {
  cleanupWeek,
  seedScenarioData,
  createAttendance,
  createShift,
  hardSync,
  syncTimecard as syncTimecardApi,
  getTimecard as getTimecardApi,
  getSyncStep1,
  addManualTimecardDay,
  approveTimecard,
  updateTimecardDay,
  findDayByCriteria,
  hardSyncRequest,
  fetchAttendance as fetchAttendanceAll,
  deleteAttendance,
  ensureEmployeeSubLocations,
  getBreakSettings,
  saveBreakSettings,
  applyClockSettings,
  restoreClockSettings,
} from './seed';
import {
  assertTimecardDays,
  assertStep4Records,
  assertStep5Records,
  assertLocationTotals,
  describeUnmatchedDays,
} from './assertions';

function effectiveClockIn(att) {
  return att.adjustedClockIn || att.clockInTime;
}

function effectiveClockOut(att) {
  return att.adjustedClockOut || att.clockOutTime || null;
}

async function fetchAttendance(client, employeeId, weekStart) {
  const endDate = addDays(weekStart, 6);
  const response = await client.get('/attendance', {
    params: { employeeId, startDate: weekStart, endDate },
  });
  return expectOk(response, 'GET /attendance');
}

async function syncTimecard(client, employeeId, weekStart, locationId) {
  const body = { employeeId, weekStartDate: weekStart };
  if (locationId) body.locationId = locationId;
  const response = await client.post('/timecards/sync', body);
  return expectOk(response, 'POST /timecards/sync');
}

async function getTimecard(client, employeeId, weekStart, locationId) {
  const params = {};
  if (locationId) params.locationId = locationId;
  const response = await client.get(`/timecards/employee/${employeeId}/week/${weekStart}`, { params });
  if (response.status === 404) return null;
  return expectOk(response, 'GET /timecards/employee/{id}/week/{date}');
}

async function runAttendanceTimecardMatch(ctx, scenario) {
  const employeeId = scenario.params.employeeId ?? ctx.defaults.employeeId;
  const weekStart = scenario.params.weekStart ?? ctx.defaults.weekStart;
  const locationId = scenario.params.locationId ?? ctx.defaults.locationId;

  const attendance = await fetchAttendance(ctx.client, employeeId, weekStart);
  if (!Array.isArray(attendance) || attendance.length === 0) {
    return {
      status: 'skipped',
      message: `No attendance records for employee ${employeeId} in week ${weekStart}`,
    };
  }

  await syncTimecard(ctx.client, employeeId, weekStart, locationId);
  const timecard = await getTimecard(ctx.client, employeeId, weekStart, locationId);
  if (!timecard?.days?.length) {
    return {
      status: 'skipped',
      message: 'Timecard has no days after sync (no shifts for this week?)',
    };
  }

  const mismatches = [];
  const daysByDate = new Map();
  for (const day of timecard.days) {
    if (!daysByDate.has(day.date)) daysByDate.set(day.date, []);
    daysByDate.get(day.date).push(day);
  }

  for (const att of attendance) {
    const date = dateFromIso(att.clockInTime);
    const candidates = daysByDate.get(date) || [];
    const expectedIn = effectiveClockIn(att);
    const expectedOut = effectiveClockOut(att);

    const matchIndex = candidates.findIndex((day) => {
      const inOk = normalizeTime(day.actualIn) === normalizeTime(expectedIn);
      const outOk = !expectedOut || normalizeTime(day.actualOut) === normalizeTime(expectedOut);
      return inOk && outOk;
    });

    if (matchIndex >= 0) {
      candidates.splice(matchIndex, 1);
      continue;
    }

    const fallbackDay = candidates[0];
    if (!fallbackDay) {
      mismatches.push({ date, field: 'day', expected: 'timecard day row', actual: 'missing' });
      continue;
    }

    if (normalizeTime(fallbackDay.actualIn) !== normalizeTime(expectedIn)) {
      mismatches.push({
        date,
        field: 'actualIn',
        expected: expectedIn,
        actual: fallbackDay.actualIn,
      });
    }
    if (expectedOut && normalizeTime(fallbackDay.actualOut) !== normalizeTime(expectedOut)) {
      mismatches.push({
        date,
        field: 'actualOut',
        expected: expectedOut,
        actual: fallbackDay.actualOut,
      });
    }
    candidates.shift();
  }

  if (mismatches.length > 0) {
    return {
      status: 'failed',
      message: `${mismatches.length} attendance/timecard mismatch(es)`,
      details: mismatches,
    };
  }

  return {
    status: 'passed',
    message: `Verified ${attendance.length} attendance record(s) against timecard days`,
  };
}

async function runSyncStep1(ctx, scenario) {
  const employeeId = scenario.params.employeeId ?? ctx.defaults.employeeId;
  const weekStart = scenario.params.weekStart ?? ctx.defaults.weekStart;
  const locationId = scenario.params.locationId ?? ctx.defaults.locationId;

  const body = { employeeId, weekStartDate: weekStart };
  if (locationId) body.locationId = locationId;
  const response = await ctx.client.post('/timecards/sync-step1', body);
  const data = await expectOk(response, 'POST /timecards/sync-step1');

  if (!data.success) {
    return { status: 'failed', message: data.message || 'sync-step1 returned success=false' };
  }
  if (!Array.isArray(data.attendanceRecords)) {
    return { status: 'failed', message: 'sync-step1 response missing attendanceRecords' };
  }

  return {
    status: 'passed',
    message: `sync-step1 ok — ${data.shifts?.length ?? 0} shift(s), ${data.attendanceRecords.length} attendance record(s)`,
    details: {
      shifts: data.shifts?.length ?? 0,
      attendanceRecords: data.attendanceRecords.length,
      step4Records: data.step4Records?.length ?? 0,
      step10Records: data.step10Records?.length ?? 0,
    },
  };
}

async function runCheckSyncNeeded(ctx, scenario) {
  const employeeId = scenario.params.employeeId ?? ctx.defaults.employeeId;
  const weekStart = scenario.params.weekStart ?? ctx.defaults.weekStart;
  const locationId = scenario.params.locationId ?? ctx.defaults.locationId;

  const params = { employeeId, weekStartDate: weekStart };
  if (locationId) params.locationId = locationId;
  const response = await ctx.client.get('/timecards/check-sync-needed', { params });
  const data = await expectOk(response, 'GET /timecards/check-sync-needed');

  if (typeof data.syncNeeded !== 'boolean') {
    return { status: 'failed', message: 'check-sync-needed did not return syncNeeded boolean' };
  }

  return {
    status: 'passed',
    message: `syncNeeded=${data.syncNeeded}${data.reason ? ` (${data.reason})` : ''}`,
    details: data,
  };
}

async function runDiscoverEmployee(ctx, scenario) {
  const weekStart = scenario.params.weekStart ?? ctx.defaults.weekStart;
  const employeeId = ctx.defaults.employeeId;
  const locationId = scenario.params.locationId ?? ctx.defaults.locationId;
  const defaults = {
    employeeId,
    locationId,
    positionId: scenario.params.positionId ?? ctx.defaults.positionId,
    scheduleId: scenario.params.scheduleId ?? ctx.defaults.scheduleId,
  };

  let attendance = await fetchAttendance(ctx.client, employeeId, weekStart);
  if (!Array.isArray(attendance) || attendance.length === 0) {
    await cleanupWeek(ctx.client, employeeId, weekStart, locationId);
    await createShift(
      ctx.client,
      { date: weekStart, start: '08:00', end: '12:00', scheduleId: defaults.scheduleId },
      defaults
    );
    await createAttendance(
      ctx.client,
      { date: weekStart, clockIn: '08:00', clockOut: '12:00', scheduleId: defaults.scheduleId },
      defaults
    );
    attendance = await fetchAttendance(ctx.client, employeeId, weekStart);
  }

  if (!Array.isArray(attendance) || attendance.length === 0) {
    return {
      status: 'skipped',
      message: `Discover only uses the QA sandbox employee (${employeeId}); could not seed attendance for week ${weekStart}`,
    };
  }

  const discoveredScenario = {
    ...scenario,
    params: {
      ...scenario.params,
      employeeId,
      weekStart,
      locationId,
    },
    type: 'attendance-timecard-match',
  };

  const result = await runAttendanceTimecardMatch(ctx, discoveredScenario);
  return {
    ...result,
    message: `${result.message} [sandbox employeeId=${employeeId}, records=${attendance.length}]`,
    details: { employeeId, attendanceCount: attendance.length, weekStart },
  };
}

async function runSeedAndAssert(ctx, scenario) {
  const params = scenario.params || {};
  const employeeId = params.employeeId ?? ctx.defaults.employeeId;
  const weekStart = params.weekStart ?? ctx.defaults.sandboxWeekStart ?? ctx.defaults.weekStart;
  const locationId = params.locationId ?? ctx.defaults.locationId;
  const defaults = {
    employeeId,
    locationId,
    positionId: params.positionId ?? ctx.defaults.positionId,
    scheduleId: params.scheduleId ?? ctx.defaults.scheduleId,
  };

  let savedBreakSettings = null;
  let savedClockSettings = null;
  try {
    if (params.breakSettings) {
      savedBreakSettings = await getBreakSettings(ctx.client);
      await saveBreakSettings(ctx.client, {
        ...savedBreakSettings,
        ...params.breakSettings,
        rules: params.breakSettings.rules ?? savedBreakSettings.rules,
      });
    }

    if (params.clockSettings) {
      savedClockSettings = await applyClockSettings(ctx.client, params.clockSettings);
    }

    if (params.cleanup !== false) {
      const cleanup = await cleanupWeek(ctx.client, employeeId, weekStart, locationId);
      if (cleanup.syncSkipped === 'approved') {
        return { status: 'skipped', message: 'Timecard week is approved — cannot cleanup/seed' };
      }
    }

    if (params.ensureSubLocations?.length) {
      await ensureEmployeeSubLocations(
        ctx.client,
        employeeId,
        params.ensureSubLocations,
        ctx.defaults.defaultAppRoleId ?? 3
      );
    }

    if (params.seed) {
      await seedScenarioData(ctx.client, params.seed, defaults);
    }

    const useHardSync = params.hardSync !== false;
    if (useHardSync) {
      await hardSync(ctx.client, employeeId, weekStart, locationId);
    } else {
      await syncTimecardApi(ctx.client, employeeId, weekStart, locationId);
    }

    const extraSyncLocations = new Set(params.syncLocations || []);
    if (params.expect?.alternateLocation?.locationId) {
      extraSyncLocations.add(params.expect.alternateLocation.locationId);
    }
    if (params.expect?.locationTotals?.rows) {
      for (const row of params.expect.locationTotals.rows) {
        if (row.locationId != null) extraSyncLocations.add(row.locationId);
      }
    }
    extraSyncLocations.delete(locationId);
    for (const locId of extraSyncLocations) {
      if (useHardSync) {
        await hardSync(ctx.client, employeeId, weekStart, locId);
      } else {
        await syncTimecardApi(ctx.client, employeeId, weekStart, locId);
      }
    }

    for (const step of params.steps || []) {
      if (step.ensureSubLocations) {
        await ensureEmployeeSubLocations(
          ctx.client,
          employeeId,
          step.ensureSubLocations,
          ctx.defaults.defaultAppRoleId ?? 3
        );
      }
      if (step.addManualDay) {
        const timecard = await getTimecardApi(ctx.client, employeeId, weekStart, locationId);
        if (!timecard?.id) {
          return { status: 'failed', message: 'No timecard exists before addManualDay step' };
        }
        await addManualTimecardDay(ctx.client, timecard.id, step.addManualDay);
      }
      if (step.addAttendance) {
        for (const att of step.addAttendance) {
          await createAttendance(ctx.client, att, defaults);
        }
      }
      if (step.addShifts) {
        for (const shift of step.addShifts) {
          await createShift(ctx.client, shift, defaults);
        }
      }
      if (step.removeAllAttendance) {
        const atts = await fetchAttendanceAll(ctx.client, employeeId, weekStart, locationId, true);
        for (const att of atts) {
          await deleteAttendance(ctx.client, att.id, true);
        }
      }
      if (step.updateDay) {
        const tc = await getTimecardApi(ctx.client, employeeId, weekStart, locationId);
        const day = findDayByCriteria(tc?.days || [], step.updateDay.match || {});
        if (!day?.id) {
          return {
            status: 'failed',
            message: `updateDay: no row matched ${JSON.stringify(step.updateDay.match)}. ${describeUnmatchedDays(tc?.days || [], step.updateDay.match || {})}`,
          };
        }
        await updateTimecardDay(ctx.client, tc.id, day.id, step.updateDay.updates);
      }
      if (step.sync) {
        await syncTimecardApi(ctx.client, employeeId, weekStart, locationId);
      }
      if (step.hardSync) {
        const response = await hardSyncRequest(ctx.client, employeeId, weekStart, locationId);
        if (step.expectBlocked) {
          if (response.status < 400) {
            return { status: 'failed', message: `Expected hardSync to be blocked but got ${response.status}` };
          }
        } else {
          await expectOk(response, 'POST /timecards/sync hardSync');
        }
      }
      if (step.updateDayExpectBlocked) {
        const tc = await getTimecardApi(ctx.client, employeeId, weekStart, locationId);
        const day = findDayByCriteria(tc?.days || [], step.updateDayExpectBlocked.match || {});
        if (!day?.id) {
          return { status: 'failed', message: 'updateDayExpectBlocked: no row found' };
        }
        const response = await ctx.client.put(`/timecards/${tc.id}/day/${day.id}`, step.updateDayExpectBlocked.updates);
        if (response.status < 400) {
          return { status: 'failed', message: `Expected day update to be blocked but got ${response.status}` };
        }
      }
      if (step.approve) {
        const timecard = await getTimecardApi(ctx.client, employeeId, weekStart, locationId);
        if (!timecard?.id) {
          return { status: 'failed', message: 'No timecard exists before approve step' };
        }
        const response = await approveTimecard(ctx.client, timecard.id);
        const blocked = response.status >= 400;
        if (step.expectBlocked && !blocked) {
          return {
            status: 'failed',
            message: `Expected approve to be blocked but got status ${response.status}`,
            details: response.data,
          };
        }
        if (step.expectSuccess && blocked) {
          return {
            status: 'failed',
            message: `Expected approve to succeed but got status ${response.status}: ${response.data?.message || ''}`,
          };
        }
      }
    }

    const timecard = await getTimecardApi(ctx.client, employeeId, weekStart, locationId);
    const step1 = params.assertStep1 ? await getSyncStep1(ctx.client, employeeId, weekStart, locationId) : null;

    const errors = [];
    assertTimecardDays(timecard?.days || [], params.expect?.timecard || {}, errors);
    if (step1) {
      assertStep4Records(step1.step4Records || [], params.expect?.step4 || {}, errors);
      assertStep5Records(step1.step5Records || [], params.expect?.step5 || {}, errors);
    }

    if (params.expect?.manualRowCount != null) {
      const manualCount = (timecard?.days || []).filter((d) => d.mode === 'Manual').length;
      if (manualCount !== params.expect.manualRowCount) {
        errors.push(`expected ${params.expect.manualRowCount} Manual row(s), got ${manualCount}`);
      }
    }

    if (params.expect?.alternateLocation) {
      const alt = params.expect.alternateLocation;
      const altTc = await getTimecardApi(ctx.client, employeeId, weekStart, alt.locationId);
      assertTimecardDays(altTc?.days || [], alt.timecard || {}, errors);
    }

    if (params.expect?.locationTotals) {
      const lt = params.expect.locationTotals;
      const excludeId = lt.excludeLocationId ?? locationId;
      const response = await ctx.client.get(
        `/timecards/employee/${employeeId}/week/${weekStart}/location-totals`,
        { params: { excludeLocationId: excludeId } }
      );
      const data = await expectOk(response, 'GET /timecards/.../location-totals');
      assertLocationTotals(data.totals || [], lt, errors);
    }

    if (errors.length > 0) {
      return {
        status: 'failed',
        message: errors.join('; '),
        details: { errors, days: timecard?.days, step4: step1?.step4Records?.length, step5: step1?.step5Records?.length },
      };
    }

    return {
      status: 'passed',
      message: `${scenario.id}: seeded week ${weekStart} and all assertions passed`,
      details: { dayCount: timecard?.days?.length ?? 0 },
    };
  } catch (err) {
    return { status: 'failed', message: err.message || String(err) };
  } finally {
    if (savedBreakSettings) {
      try {
        await saveBreakSettings(ctx.client, savedBreakSettings);
      } catch (restoreErr) {
        console.warn(`[${scenario.id}] Failed to restore break settings:`, restoreErr.message);
      }
    }
    if (savedClockSettings) {
      try {
        await restoreClockSettings(ctx.client, savedClockSettings);
      } catch (restoreErr) {
        console.warn(`[${scenario.id}] Failed to restore clock settings:`, restoreErr.message);
      }
    }
  }
}

const RUNNERS = {
  'attendance-timecard-match': runAttendanceTimecardMatch,
  'sync-step1': runSyncStep1,
  'check-sync-needed': runCheckSyncNeeded,
  discover: runDiscoverEmployee,
  'seed-and-assert': runSeedAndAssert,
};

async function runScenario(ctx, scenario) {
  const runner = RUNNERS[scenario.type];
  if (!runner) {
    return { status: 'failed', message: `Unknown scenario type: ${scenario.type}` };
  }
  const canonical = ctx.config?.defaults || ctx.defaults;
  const defaults = ctx.defaults || canonical;
  const remapped = {
    ...scenario,
    params: alignScenarioWeekStart(
      remapSandboxIds(scenario.params || {}, canonical, defaults),
      defaults.weekStartsOn || 'monday',
      defaults.sandboxWeekStart || defaults.weekStart
    ),
  };
  return runner({ ...ctx, defaults }, remapped);
}

export { runScenario, RUNNERS, remapSandboxIds };
